import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import './../XlsxTable.css';
import { Button, Modal, } from 'react-bootstrap';
import instance from "../../services/baseService";
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import NoteModal from "./../common/NoteModal";
import { useParams } from "react-router-dom";
import { CRUD_PERMISSIONS } from "../../constants/user";
import useCheckAuthorization from "../../hooks/checkAuthorization";
import ApproveRejectOptions from './../common/ApproveRejectOptions';


function GSTR9cSheetModal({ showSheetModal, handleSheetClose, handleSheetSubmit, handleSheetSubmitManager, sheetSummaryData, viewOnlySheetData, getStatus, hasSubmitAccess, showSkipModal, handleSkipClose, handleSkipSubmit, setShowSkipModal }) {
    const { selectedMonth, selectedYear, selectedStartDate, selectedEndDate } = useSelector((state) => state.common);
    const [loading, setLoading] = useState(false);
    //const [fileUrl, setFileUrl] = useState('');
    const [allCells, setAllCells] = useState([]);
    const [refreshAllCells, setRefreshAllCells] = useState(false);
    const [status, setStatus] = useState(4);
    const [data, setData] = useState([]);
    const [maxColumns, setMaxColumns] = useState(0);
    const [modalInfo, setModalInfo] = useState({
        isVisible: false,
        cell: null,
        note: '',
        noteArr: []
    });
    const { client_id } = useParams();
    const manageAllClientsPermission = useCheckAuthorization(CRUD_PERMISSIONS.sheet.manage_clients);

    const getExcelCellName = ({ row, col }) => {
        const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

        let colName = '';
        while (col >= 0) {
            colName = letters[col % 26] + colName;
            col = Math.floor(col / 26) - 1;
        }

        const rowName = (row + 1).toString();
        return `${colName}${rowName}`;
    };

    // Helper function to generate column letters dynamically
    const getColumnLetter = (index) => {
        let result = '';
        while (index >= 0) {
            result = String.fromCharCode(65 + (index % 26)) + result;
            index = Math.floor(index / 26) - 1;
        }
        return result;
    };

    function reverseExcelName(cellName) {
        const match = cellName.match(/^([A-Z]+)(\d+)$/);
        if (!match) {
            throw new Error('Invalid cell name');
        }

        const colLetters = match[1];
        const row = parseInt(match[2], 10) - 1; // Subtract 1 for zero-based index

        // Convert column letters to a number, starting from 0
        let col = 0;
        for (let i = 0; i < colLetters.length; i++) {
            col = col * 26 + (colLetters.charCodeAt(i) - 65);
        }
        return { col, row };
    }

    const addComment = async (note) => {
        try {
            const promise = instance.post(`/api/v1/client/gstr9c/add/comment/${client_id}`, {
                start_date: selectedStartDate,
                end_date: selectedEndDate,
                comment: note,
                cell_no: getExcelCellName({
                    row: modalInfo.cell.row,
                    col: modalInfo.cell.col
                }),
                status: status
            });

            const res = await toast.promise(promise, {
                loading: 'Saving...',
                success: <b>Comment saved!</b>,
                error: <b>Could not save.</b>
            });
            const noteArr = [...modalInfo.cell.noteArr, res.data.data];
            updateNote(note, { row: modalInfo.cell.row, col: modalInfo.cell.col }, noteArr);
        } catch (error) {
            console.error('error', error);
        }
    };

    const fetchAndProcessFile = async (fileUrl) => {
        try {
            setLoading(true);
            const response = await fetch(fileUrl);
            const arrayBuffer = await response.arrayBuffer();
            const data = new Uint8Array(arrayBuffer);

            // Read workbook with minimal processing
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheet = workbook.Sheets[workbook.SheetNames[0]];

            // Get the range of the sheet
            const range = XLSX.utils.decode_range(firstSheet['!ref']);
            const rows = [];

            // Helper function to convert Excel date serial to MM/YYYY format
            const excelDateToMMYYYY = (serialNumber) => {
                // Excel date serial number to JavaScript Date
                const excelEpoch = new Date(1900, 0, 1);
                const jsDate = new Date(excelEpoch.getTime() + (serialNumber - 2) * 24 * 60 * 60 * 1000);

                const month = String(jsDate.getMonth() + 1).padStart(2, '0');
                const year = jsDate.getFullYear();
                return `${month}/${year}`;
            };

            // Helper function to format numbers in Indian numbering system
            const formatIndianNumber = (number) => {
                // Convert to string and handle decimals
                const numStr = String(number);
                const parts = numStr.split('.');
                let integerPart = parts[0];
                const decimalPart = parts[1];

                // Remove any existing commas
                integerPart = integerPart.replace(/,/g, '');

                // Convert to Indian numbering system
                if (integerPart.length <= 3) {
                    // Numbers with 3 or fewer digits don't need commas
                    return decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
                }

                // For numbers with more than 3 digits
                let result = '';
                let digitCount = 0;

                // Process from right to left
                for (let i = integerPart.length - 1; i >= 0; i--) {
                    result = integerPart[i] + result;
                    digitCount++;

                    // Add comma after first 3 digits, then every 2 digits
                    if (digitCount === 3 && i > 0) {
                        result = ',' + result;
                    } else if (digitCount > 3 && (digitCount - 3) % 2 === 0 && i > 0) {
                        result = ',' + result;
                    }
                }

                return decimalPart ? `${result}.${decimalPart}` : result;
            };

            // Helper function to detect if this looks like a date that should be MM/YYYY
            const shouldConvertToMMYYYY = (cell) => {
                // Check if it's a number type with a date-like formatted display
                if (cell.t === 'n' && cell.w) {
                    // Check if the formatted text looks like a date (contains month abbreviations)
                    const monthPattern = /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)-\d{2}$/;
                    return monthPattern.test(cell.w.trim());
                }
                return false;
            };

            // Helper function to detect if this is a number that should be formatted in Indian style
            const shouldFormatAsIndianNumber = (cell) => {
                // Check if it's a number type with comma-separated formatting
                if (cell.t === 'n' && cell.w) {
                    const trimmed = cell.w.trim();
                    // Check if it contains commas and looks like a formatted number
                    return /^\s*[\d,]+\.?\d*\s*$/.test(trimmed) && trimmed.includes(',');
                }
                return false;
            };

            // Extract data row by row with intelligent formatting
            for (let R = range.s.r; R <= range.e.r; ++R) {
                const row = [];
                for (let C = range.s.c; C <= range.e.c; ++C) {
                    const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
                    const cell = firstSheet[cellAddress];

                    if (cell) {
                        let cellValue;

                        // Special handling for date-like cells that should be MM/YYYY
                        if (shouldConvertToMMYYYY(cell)) {
                            cellValue = excelDateToMMYYYY(cell.v);
                        }
                        // Special handling for numbers that should be formatted in Indian style
                        else if (shouldFormatAsIndianNumber(cell)) {
                            cellValue = formatIndianNumber(cell.v);
                        }
                        // Use formatted text (w) if available, otherwise use value
                        else if (cell.w !== undefined && cell.w !== null) {
                            cellValue = String(cell.w).trim();
                        }
                        else if (cell.v !== undefined && cell.v !== null) {
                            cellValue = String(cell.v);
                        }
                        else {
                            cellValue = '';
                        }

                        row[C] = cellValue;
                    } else {
                        row[C] = undefined;
                    }
                }
                rows.push(row);
            }

            // Calculate maximum columns dynamically
            let maxCols = 0;
            rows.forEach(row => {
                if (row && row.length > maxCols) {
                    maxCols = row.length;
                }
            });

            setData(rows);
            setMaxColumns(maxCols);
            setRefreshAllCells((ref) => !ref);
        } catch (error) {
            console.error('Failed to fetch and process the file', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCellRightClick = (rowIndex, colIndex, e) => {
        e.preventDefault();
        if (!viewOnlySheetData) {
            const cellData = data[rowIndex][colIndex];
            const cell = {
                row: rowIndex,
                col: colIndex,
                note: cellData?.note || '',
                noteArr: cellData?.noteArr || []
            };

            setModalInfo({ ...modalInfo, isVisible: true, cell });
        }
    };

    const saveNote = (note, rowColObj, noteArr,) => {
        if (note) {
            addComment(note);
            const updatedData = [...data];
            const { row, col } = rowColObj ? rowColObj : modalInfo.cell;

            if (updatedData[row]) {
                if (!updatedData[row][col]?.note) {
                    updatedData[row][col] = { value: updatedData[row][col], note, noteArr };
                } else if (updatedData[row][col]) {
                    updatedData[row][col].note = note;
                } else {
                    // Handle the case where col doesn't exist in the row
                    // updatedData[row][col] = { value: "", note }; // or handle as needed
                }
            } else {
                // Handle the case where the row doesn't exist
                updatedData[row] = [];
                updatedData[row][col] = { value: '', note, noteArr }; // or handle as needed
            }
            setData(updatedData);
            fetchAndProcessFile(sheetSummaryData?.sheet?.url);
            setModalInfo({ ...modalInfo, isVisible: false, cell: null, note: '', noteArr });
        }
    };

    const updateNote = (note, rowColObj, noteArr,) => {
        if (note) {
            //addComment(note);
            //console.log('1 data', data);
            const updatedData = [...data];
            const { row, col } = rowColObj ? rowColObj : modalInfo.cell;

            if (updatedData[row]) {
                if (!updatedData[row][col]?.note) {
                    updatedData[row][col] = { value: updatedData[row][col], note, noteArr };
                } else if (updatedData[row][col]) {
                    updatedData[row][col].note = note;
                    updatedData[row][col].noteArr = noteArr;
                } else {
                    // Handle the case where col doesn't exist in the row
                    // updatedData[row][col] = { value: "", note }; // or handle as needed
                }
            } else {
                // Handle the case where the row doesn't exist
                updatedData[row] = [];
                updatedData[row][col] = { value: '', note, noteArr }; // or handle as needed
            }
            setData(updatedData);
            setModalInfo({ ...modalInfo, isVisible: false, cell: null, note: '', noteArr });
        }
    };


    const isCellHighlighted = (rowIndex, colIndex) => {
        const cellName = getExcelCellName({ row: rowIndex, col: colIndex });
        return allCells?.some((cell) => cell.cell_no === cellName);
    };

    useEffect(() => {
        if (!viewOnlySheetData) {
            const validCells = allCells.filter((cell) => cell.cell_no !== 'None');
            validCells.forEach((cell) => {
                const { row, col } = reverseExcelName(cell.cell_no);
                const note = cell.comments?.[0].comment;
                const noteArr = cell.comments;
                if (note) {
                    updateNote(note, { row, col }, noteArr);
                }
            });
        }
    }, [allCells, refreshAllCells, viewOnlySheetData]);

    useEffect(() => {
        if (sheetSummaryData && !viewOnlySheetData) {
            setAllCells(sheetSummaryData.all_cell);
            fetchAndProcessFile(sheetSummaryData?.sheet?.url);
        }
        else if (viewOnlySheetData) {
            setAllCells([]);
            fetchAndProcessFile(viewOnlySheetData.url);
        }
    }, [sheetSummaryData, viewOnlySheetData, showSheetModal]);

    return (
        <>
            {/* Modal structure */}
            <Modal size="xl" show={showSheetModal} onHide={handleSheetClose} backdrop="static" enforceFocus={false} className="excel-sheet-modal">
                <Modal.Header closeButton>
                    <Modal.Title>{!viewOnlySheetData ? sheetSummaryData.sheet?.file_name : viewOnlySheetData.file_name}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {loading && data ? (
                        <p className="text-center my-4">...loading</p>
                    ) : (
                        <div className="">
                            <div className="table-container">
                                <table>
                                    <thead>
                                        <tr>
                                            <th></th>
                                            {/* Dynamically generate column headers based on maxColumns */}
                                            {data[0] && Array.from({ length: maxColumns }).map((_, index) => (
                                                <th key={index}>{getColumnLetter(index)}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data[0] && data.map((row, rowIndex) =>
                                            rowIndex >= 8 ? (
                                                <tr key={rowIndex}>
                                                    <th>{rowIndex + 1}</th>
                                                    {row.length === 1 ? (
                                                        <td className="bg-[#eff2f3] text-[#1e4466]" colSpan={maxColumns}>
                                                            {typeof row[0] === 'object' && row[0] !== null
                                                                ? row[0]?.value !== undefined && row[0]?.value !== null
                                                                    ? row[0]?.value
                                                                    : ''
                                                                : row[0] !== undefined && row[0] !== null
                                                                    ? row[0]
                                                                    : ''}
                                                        </td>
                                                    ) : (
                                                        /* Dynamically generate cells based on maxColumns */
                                                        Array.from({ length: maxColumns }).map((_, colIndex) => (
                                                            <td
                                                                // className={`${rowIndex === 8 ? 'bg-[#0d4473] text-white' : ''}`}
                                                                id={getExcelCellName({
                                                                    row: rowIndex,
                                                                    col: colIndex
                                                                })}
                                                                key={colIndex}
                                                                onContextMenu={(e) => handleCellRightClick(rowIndex, colIndex, e)}
                                                                style={{
                                                                    backgroundColor: isCellHighlighted(rowIndex, colIndex)
                                                                        ? '#FFFFCC'
                                                                        : row[colIndex]?.note
                                                                            ? '#FFFFCC'
                                                                            : ''
                                                                }}>
                                                                {typeof row[colIndex] === 'object' && row[colIndex] !== null
                                                                    ? row[colIndex]?.value !== undefined && row[colIndex]?.value !== null
                                                                        ? row[colIndex]?.value
                                                                        : ''
                                                                    : row[colIndex] !== undefined && row[colIndex] !== null
                                                                        ? row[colIndex]
                                                                        : ''}
                                                            </td>
                                                        ))
                                                    )}
                                                </tr>
                                            ) : (
                                                <tr key={rowIndex}>
                                                    <td>{rowIndex + 1}</td>
                                                    {row?.length === 1 ? (
                                                        <td className="bg-[#eff2f3] text-[#1e4466]" colSpan={maxColumns}>
                                                            {typeof row[0] === 'object' && row[0] !== null
                                                                ? row[0]?.value !== undefined && row[0]?.value !== null
                                                                    ? row[0]?.value
                                                                    : ''
                                                                : row[0] !== undefined && row[0] !== null
                                                                    ? row[0]
                                                                    : ''}
                                                        </td>
                                                    ) : (
                                                        /* Dynamically generate cells based on maxColumns */
                                                        Array.from({ length: maxColumns }).map((_, colIndex) => (
                                                            <td
                                                                key={colIndex}
                                                                id={getExcelCellName({
                                                                    row: rowIndex,
                                                                    col: colIndex
                                                                })}
                                                                onContextMenu={(e) => handleCellRightClick(rowIndex, colIndex, e)}
                                                                style={{
                                                                    backgroundColor: isCellHighlighted(rowIndex, colIndex)
                                                                        ? '#FFFFCC'
                                                                        : row[colIndex] && row[colIndex]?.note
                                                                            ? '#FFFFCC'
                                                                            : ''
                                                                }}>
                                                                {typeof row[colIndex] === 'object' && row[colIndex] !== null
                                                                    ? row[colIndex]?.value !== undefined && row[colIndex]?.value !== null
                                                                        ? row[colIndex]?.value
                                                                        : ''
                                                                    : row[colIndex] !== undefined && row[colIndex] !== null
                                                                        ? row[colIndex]
                                                                        : ''}
                                                            </td>
                                                        ))
                                                    )}
                                                </tr>
                                            )
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" size="sm" onClick={handleSheetClose}>
                        Close
                    </Button>
                    {!viewOnlySheetData && <ApproveRejectOptions manageAllClientsPermission={manageAllClientsPermission} sheetSummaryData={sheetSummaryData} loading={loading} handleSheetSubmit={handleSheetSubmit} handleSheetClose={handleSheetClose} handleSheetSubmitManager={handleSheetSubmitManager} getStatus={getStatus} hasSubmitAccess={hasSubmitAccess} showSkipModal={showSkipModal} handleSkipClose={handleSkipClose} handleSkipSubmit={handleSkipSubmit} setShowSkipModal={setShowSkipModal} />}

                </Modal.Footer>
            </Modal>
            {modalInfo.isVisible && (
                <NoteModal
                    noteArr={modalInfo.cell.noteArr}
                    note={modalInfo.cell.note}
                    onSave={saveNote}
                    status={status}
                    setStatus={setStatus}
                    onClose={() => setModalInfo({ ...modalInfo, isVisible: false, cell: null, note: '' })}
                />
            )}
        </>
    );
}

export default GSTR9cSheetModal;